import React from 'react';

import { Flex } from '@chakra-ui/react';
import {
  ALLOWED_STEPS_REVIEW_IN_PROGRESS,
  ALLOWED_STEPS_REVIEW_PUBLISHED,
  REVIEW_CYCLE_STEPS_METADATA,
  REVIEW_CYCLE_STEPS_ORDER,
  REVIEW_CYCLE_STEPS_V1,
  REVIEW_FORM_STAGE,
  useCalibrationScoreSettings,
  useElements,
  useElementsScoreSettings,
  useFormFillings,
  useIsAllEmployeesSelected,
  useIsRevieweesCustomViewsSettings,
  useIsReviewsOverlappingEmployees,
  useReviewCreationData,
  useReviewTypeAndDetails,
  useReviewees,
  useReviewers,
  useShareResults,
  useTimelines,
} from 'reducers/reviewCreationSlice';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { LinearScale } from 'assets/assetsV1/iconComponents';

import { Divider } from 'uiToolkitV1/Divider';
import { FormTabToggle } from 'uiToolkitV1/FormTabToggle';
import { isScoreValidator } from 'uiToolkitV1/ScoreSettingTable/ScoreSettingTable';
import { TextBodyLarge } from 'uiToolkitV1/Typography';

import { useSaveCalibrationSettings } from '../Hooks/dataHooks/calibrationDataHooks';
import { useSaveElements } from '../Hooks/dataHooks/elementsDataHooks';
import { useSaveFormFillings } from '../Hooks/dataHooks/formFillingsDataHooks';
import { useSaveReviewTypeAndDetails } from '../Hooks/dataHooks/reviewTypeAndDetailsDataHooks';
import { useSaveReviewees } from '../Hooks/dataHooks/revieweesDataHooks';
import { useSaveReviewers } from '../Hooks/dataHooks/reviewersDataHooks';
import { useSaveShareResults } from '../Hooks/dataHooks/shareResultsDataHooks';
import { useSaveTimelines } from '../Hooks/dataHooks/timelinesDataHooks';
import { useReviewCreationNavigation } from '../Hooks/useReviewCreationNavigation';
import { AnswerTypes } from '../Steps/Calibration/ScoreSection';
import { isScoreNameValid } from '../Steps/Calibration/TextScoreSettingTable';
import { hasAllTemplatesSelected, hasAllValidScoring, hasValidRating } from '../Steps/Elements/Elements';
import { validateTimelinesDates } from '../Steps/Timelines/Timelines';

const Header = () => (
  <Flex gap={3} py="14px" pl={3}>
    <LinearScale boxSize="24px" color="icon.g2" />
    <TextBodyLarge>Review Design</TextBodyLarge>
  </Flex>
);

const calcOptions = ({ lastEnabledSection, currentStage }) => {
  // filter validation step if not in draft stage
  const validReviewCycleSteps =
    currentStage === REVIEW_FORM_STAGE.DRAFT
      ? REVIEW_CYCLE_STEPS_ORDER
      : REVIEW_CYCLE_STEPS_ORDER.filter((step) => step !== REVIEW_CYCLE_STEPS_V1.VALIDATION);

  const lastEnabledSectionIndex = validReviewCycleSteps.indexOf(lastEnabledSection);

  const isCompleted = (step) =>
    validReviewCycleSteps.indexOf(step) < validReviewCycleSteps.indexOf(lastEnabledSection || step);

  const calcIsDisabled = (step) => {
    const stepIndex = validReviewCycleSteps.indexOf(step);
    if (currentStage === REVIEW_FORM_STAGE.DRAFT) {
      return stepIndex > lastEnabledSectionIndex || stepIndex === validReviewCycleSteps.length - 1;
    }
    if (currentStage === REVIEW_FORM_STAGE.PUBLISHED) {
      return ALLOWED_STEPS_REVIEW_PUBLISHED.indexOf(step) === -1;
    }
    return ALLOWED_STEPS_REVIEW_IN_PROGRESS.indexOf(step) === -1;
  };

  return validReviewCycleSteps.map((step) => {
    const stepData = {
      title: REVIEW_CYCLE_STEPS_METADATA[step].title,
      id: step,
      isDisabled: calcIsDisabled(step),
      isComplete: isCompleted(step),
    };
    return stepData;
  });
};

const ProcessStepperV1 = () => {
  const { currentStage, step, reviewCycleId, lastEnabledSection } = useReviewCreationData();

  const options = calcOptions({
    lastEnabledSection,
    currentStage,
    reviewCycleId,
    step,
  });

  const { name, startDate, endDate } = useReviewTypeAndDetails();
  const isReviewTypeContinueDisabled = !name || !startDate || !endDate;
  const saveDataMutationTypeAndDetails = useSaveReviewTypeAndDetails();

  const revieweesViews = useReviewees();
  const isRevieweesCustomViewsSettings = useIsRevieweesCustomViewsSettings();
  const isAllEmployeesSelected = useIsAllEmployeesSelected();
  const isReviewsOverlappingEmployees = useIsReviewsOverlappingEmployees();
  const isReviweesContinueDisabled =
    (!isAllEmployeesSelected && revieweesViews?.length === 0) ||
    (isRevieweesCustomViewsSettings && isReviewsOverlappingEmployees);
  const saveDataMutationReviewees = useSaveReviewees();

  const reviewersViews = useReviewers();
  const isReviewersContinueDisabled = reviewersViews?.length === 0;

  const saveDataMutationReviewers = useSaveReviewers();
  const elementsViews = useElements();
  const feedbackFormId = elementsViews?.map((item) => item.feedbackFormId);
  const { isGoalRatingAutoComputed, autoComputeGoalRatingSettings } = useElementsScoreSettings({
    feedbackFormId: feedbackFormId[0],
  });
  const isElementContinueDisabled =
    !hasAllTemplatesSelected({ elements: elementsViews }) ||
    !hasAllValidScoring({ elements: elementsViews }) ||
    hasValidRating({ isGoalRatingAutoComputed, autoComputeGoalRatingSettings });
  const saveDataMutationElements = useSaveElements();

  const formFillingSettings = useFormFillings();
  const isFormFillingContinueDisabled = formFillingSettings?.length === 0;
  const saveDataMutationFormFillings = useSaveFormFillings({ isPreviewClick: true });

  const { answerOptions, answerType } = useCalibrationScoreSettings();

  let isValidScoreSettings = false;
  if (answerType === AnswerTypes.NUMBER_RATING) {
    // because we have fixed options in dropdown
    isValidScoreSettings = true;
  } else {
    const isValidScoreNames = isScoreNameValid(answerOptions);
    const isScoreValid = isScoreValidator(answerOptions);
    isValidScoreSettings = isValidScoreNames && isScoreValid;
  }
  const isCalibrationContinueDisabled = !isValidScoreSettings;
  const saveDataMutationCalibration = useSaveCalibrationSettings();

  const shareResults = useShareResults();
  const isShareContinueDisabled = shareResults?.length === 0;
  const saveDataMutationShareResults = useSaveShareResults({ isPreviewClick: true });
  const timelines = useTimelines();
  const isValidTimelines = validateTimelinesDates(timelines);
  const saveDataMutationTimelines = useSaveTimelines();

  const { goToStep } = useReviewCreationNavigation();

  return (
    <Flex
      flexDir="column"
      p={checkNewNavEnabled() ? 0 : 3}
      pt={checkNewNavEnabled() ? 0 : 2}
      position="relative"
      flexGrow={1}
      overflowY="auto"
    >
      <Header />
      <Divider mb={2} borderColor="border.CFD7DF" />

      <FormTabToggle
        options={options}
        selectedOptionId={step}
        changeHandler={(clickedStep) => {
          switch (step) {
            case REVIEW_CYCLE_STEPS_V1.NAME:
              if (isReviewTypeContinueDisabled === false) {
                saveDataMutationTypeAndDetails.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            case REVIEW_CYCLE_STEPS_V1.REVIEWEES:
              if (isReviweesContinueDisabled === false) {
                saveDataMutationReviewees.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            case REVIEW_CYCLE_STEPS_V1.REVIEWERS:
              if (isReviewersContinueDisabled === false) {
                saveDataMutationReviewers.mutate({ nextStep: clickedStep });
                return;
              }
              break;

            case REVIEW_CYCLE_STEPS_V1.ELEMENTS:
              if (isElementContinueDisabled === false) {
                saveDataMutationElements.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            case REVIEW_CYCLE_STEPS_V1.FORM_FILLING_EXPERIENCE:
              if (isFormFillingContinueDisabled === false) {
                saveDataMutationFormFillings.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            case REVIEW_CYCLE_STEPS_V1.CALIBRATION:
              if (isCalibrationContinueDisabled === false) {
                saveDataMutationCalibration.mutate({ nextStep: clickedStep });
                return;
              }
              break;

            case REVIEW_CYCLE_STEPS_V1.SHARE_RESULTS:
              if (isShareContinueDisabled === false) {
                saveDataMutationShareResults.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            case REVIEW_CYCLE_STEPS_V1.TIMELINES:
              if (isValidTimelines) {
                saveDataMutationTimelines.mutate({ nextStep: clickedStep });
                return;
              }
              break;
            default:
          }
          goToStep(clickedStep);
        }}
        isRedCurrentStepStyle
        withMargin
      />
    </Flex>
  );
};

export { ProcessStepperV1 };
